import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import cookies from '../utils/cookies';
import { useFormik } from 'formik';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import Input from '../components/form/Input';
import { ReactComponent as PassportLogo } from '../svg/passport_logo.svg';
import passwordValidator from 'password-validator';
import { useDispatch } from 'react-redux';
import { loadAccount, sessionLoginSuccess } from '../slices/sessionSlice';
import { FooterCopy } from '../components/nav/Footer';

export const passwordSchema = new passwordValidator();

passwordSchema.is().min(8).has().uppercase().has().lowercase().has().digits().has().symbols();

export const LoginFooter = () => (
    <div className="login-footer">
        <div className="footer-links">
            <Link to="/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Use</Link>
            <span className="separator">|</span>
            <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
        </div>
        <p>
            <small>
                <FooterCopy />
            </small>
        </p>
    </div>
);

const Login = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const history = useHistory();

    useEffect(() => {
        if (cookies.get('auth_token')) {
            const from = history.location.state?.from?.pathname || '/home';
            history.push(from);
        }
    }, [history]);
    const [error, setError] = useState(null);
    const [newPasswordReqd, setNewPasswordReqd] = useState(false);
    const [newPasswordInfo, setNewPasswordInfo] = useState(null);
    const [valid, setValid] = useState([]);


    let { values, setFieldValue } = useFormik({
        initialValues: {
            email: '',
            password: ''
        }
    });

    const validate = () => {
        return Object.keys(values).reduce((value, input) => {
            if (!values[input].current) return value;
            let val = values[input].current.validate();
            if (!val && value) values[input].current.focus();
            return value && val;
        }, true);
    };

    const interpretError = (err) => {
        if (!err.response) {
            return 'Error logging in.';
        }
        if (err.response.data.message.toLowerCase() === 'passwordresetrequiredexception') {
            return 'Please reset your password by clicking "Forgot Password?" above.';
        }
        return err.response.data.message;
    };

    const handlePostLogin = async (data) => {
        try {
            if (!data) {
                throw new Error('No Response');
            }

            if (data.ChallengeName && data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
                setNewPasswordReqd(true);
                setNewPasswordInfo({ ...data.ChallengeParameters, session: data.Session });
                setFieldValue('password', '');

                return data;
            }

            if (!data.AuthenticationResult) {
                throw new Error('No Authentication Token');
            }

            cookies.set('refresh_token', data.AuthenticationResult.RefreshToken, { path: '/', maxAge: 3600 });
            cookies.set('auth_token', data.AuthenticationResult.IdToken, { path: '/', maxAge: 3600 });
            cookies.set('access_key', data.AuthenticationResult.AccessToken, { path: '/', maxAge: 3600 });

            let account = await loadAccount(data.AuthenticationResult.IdToken);
            dispatch(sessionLoginSuccess(account));

            if (history.location.state?.from?.pathname) {
                history.push(history.location.state?.from?.pathname);
            } else {
                history.push('/home');
            }
        } catch (error) {
            console.error(error);
        }
    };

    const processLogin = async (event) => {
        event.preventDefault();
        try {
            if (!validate()) return;

            setLoading(true);

            let { data } = await axios.request({
                url: `/unauthorized/login`,
                method: 'POST',
                data: {
                    username: values.email,
                    password: values.password
                }
            });
            await handlePostLogin(data);
        } catch (err) {
            setLoading(false);
            setError(interpretError(err));
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordSet = (event) => {
        event.preventDefault();

        if (!validate()) return;

        setLoading(true);

        axios
            .request(`/unauthorized/newpasswordchallenge`, {
                method: 'POST',
                data: {
                    username: newPasswordInfo.USER_ID_FOR_SRP,
                    newpassword: values.password,
                    session: newPasswordInfo.session
                }
            })
            .then(({ data }) => handlePostLogin(data))
            .catch((err) => {
                setLoading(false);
                setError(interpretError(err));
            });
    };

    return (
        <div className="page-signin">
            <Helmet>
                <title>Login</title>
            </Helmet>
            <PassportLogo className="logo" width="150" />
            {newPasswordReqd && (
                <div className="row mb-5">
                    {error && (
                        <div className="col-xs-12 col-md-6 col-md-offset-3">
                            <div className="flash flash-danger mb-4">
                                <p>{error}</p>
                            </div>
                        </div>
                    )}
                    <form className="col-xs-12 col-md-3 col-md-offset-4" onSubmit={handlePasswordSet} noValidate>
                        <p>You need to set a new password.</p>
                        <Input
                            label="New Password"
                            type="password"
                            id="password"
                            value={values.password}
                            autoComplete="new-password"
                            validators={['validPassword']}
                            isRequiredMsg="Password is required."
                            required
                            onChange={(id, value) => {
                                setFieldValue('password', value);
                                setValid(passwordSchema.validate(value, { list: true }));
                            }}
                        />
                        <button className="btn btn-primary" type="submit" disabled={loading}>
                            {loading ? 'Saving...' : 'Save'}
                        </button>
                    </form>
                    <div className="col-xs-12 col-md-2">
                        <div className="card card-flat password-reqs">
                            <div className="card-header">
                                <h6>Password Requirements</h6>
                            </div>
                            <div className="card-body">
                                <ul className="list-unstyled">
                                    <li>
                                        {!valid.includes('min') && !!values.password.length && (
                                            <span className="indicator indicator-success"></span>
                                        )}{' '}
                                        Minimum 8 characters
                                    </li>
                                    <li>
                                        {!valid.includes('digits') && !!values.password.length && (
                                            <span className="indicator indicator-success"></span>
                                        )}{' '}
                                        Include numbers
                                    </li>
                                    <li>
                                        {!valid.includes('symbols') && !!values.password.length && (
                                            <span className="indicator indicator-success"></span>
                                        )}{' '}
                                        Include special character
                                    </li>
                                    <li>
                                        {!valid.includes('uppercase') && !!values.password.length && (
                                            <span className="indicator indicator-success"></span>
                                        )}{' '}
                                        Include uppercase letters
                                    </li>
                                    <li>
                                        {!valid.includes('lowercase') && !!values.password.length && (
                                            <span className="indicator indicator-success"></span>
                                        )}{' '}
                                        Include lowercase letters
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!newPasswordReqd && (
                <div className="form-signin">
                    {history.location?.state?.reason === 'session_expired' && (
                        <div className="flash flash-warning mb-4">
                            <p>Your session expired. Please log in again.</p>
                        </div>
                    )}
                    <form onSubmit={processLogin} noValidate>
                        {error && (
                            <div className="flash flash-danger mb-4">
                                <p>{error}</p>
                            </div>
                        )}
                        <Input
                            label="Email"
                            type="email"
                            id="inputEmail"
                            value={values.email}
                            autoComplete="email"
                            isRequiredMsg="Email is required."
                            required
                            autoFocus
                            onChange={(id, value) => setFieldValue('email', value)}
                        />
                        <Input
                            label="Password"
                            type="password"
                            id="password"
                            value={values.passowrd}
                            autoComplete="current-password"
                            isRequiredMsg="Password is required."
                            required
                            onChange={(id, value) => setFieldValue('password', value)}
                        />
                        <button className="btn btn-primary btn-block mb-3" type="submit" disabled={loading}>
                            {loading ? 'Signing in...' : 'Sign in'}
                        </button>

                        <p className="text-center">
                            <small>
                                <Link
                                    to={{
                                        pathname: '/forgot-password',
                                        search: values.email ? `?email=${values.email}` : undefined
                                    }}
                                >
                                    Forgot Password?
                                </Link>
                            </small>
                        </p>
                    </form>
                </div>
            )}

            <LoginFooter />
        </div>
    );
};

export default Login;
